<template>
  <!--
    Редактор мест показывает только разрешённые к приёму связки и группирует
    конкурсные группы по филиалу, специальности и форме обучения.
  -->
  <v-card class="competition-config" elevation="0">
    <v-card-title class="competition-config__header">
      <div>
        <h2 class="competition-config__title">Конкурсные группы</h2>
        <p class="competition-config__description">
          Настройте бюджетные, договорные и целевые места по специальностям.
        </p>
      </div>
    </v-card-title>

    <v-card-text class="competition-config__content">
      <v-alert
        v-if="error"
        class="competition-config__alert"
        type="error"
        variant="tonal"
        closable
        @click:close="error = ''"
      >
        {{ error }}
      </v-alert>

      <div v-if="loading">
        <v-skeleton-loader type="heading, list-item-two-line, list-item-two-line, article" />
      </div>

      <template v-else>
        <div class="competition-config__filters">
          <v-select
            v-model="selectedDepartmentId"
            :items="departmentOptions"
            item-title="name"
            item-value="id"
            label="Филиал"
            variant="outlined"
            hide-details
          />
        </div>

        <v-alert v-if="!departments.length" type="info" variant="tonal">
          В структуре образовательной организации нет доступных специальностей.
        </v-alert>

        <v-alert
          v-else-if="!displayedSpecialtyCount"
          type="info"
          variant="tonal"
        >
          Для выбранного филиала нет доступных специальностей.
        </v-alert>

        <div v-else class="competition-config__department-groups">
          <section
            v-for="department in displayedDepartmentGroups"
            :key="department.id"
            class="competition-config__department-group"
          >
            <div
              v-if="isAllDepartments"
              class="competition-config__department-heading"
            >
              <h3>{{ department.name }}</h3>
              <v-chip variant="tonal" size="small">
                Специальностей: {{ department.specialties.length }}
              </v-chip>
            </div>

            <v-expansion-panels
              class="competition-config__specialties"
              multiple
            >
              <v-expansion-panel
                v-for="specialty in department.specialties"
                :key="specialty.id"
                :value="specialty.id"
                class="competition-config__specialty"
                elevation="0"
              >
                <v-expansion-panel-title>
                  <div class="competition-config__specialty-heading">
                    <div>
                      <span class="competition-config__specialty-code">
                        {{ specialty.code || 'Без кода' }}
                      </span>
                      <strong>{{ specialty.name }}</strong>
                    </div>
                    <v-chip
                      :color="specialtyCompetitionCount(specialty) ? 'primary' : 'default'"
                      variant="tonal"
                      size="small"
                    >
                      Конкурсов: {{ specialtyCompetitionCount(specialty) }}
                    </v-chip>
                  </div>
                </v-expansion-panel-title>

                <v-expansion-panel-text>
                  <div class="competition-config__specialty-toolbar">
                    <div class="competition-config__study-list">
                      <v-chip
                        v-for="study in specialty.studies"
                        :key="study.id"
                        :color="study.active ? 'success' : 'default'"
                        variant="tonal"
                        size="small"
                      >
                        {{ study.name }}
                      </v-chip>
                    </div>

                    <v-btn
                      color="primary"
                      variant="tonal"
                      prepend-icon="mdi-plus"
                      @click="openEditor(specialty)"
                    >
                      Добавить конкурс
                    </v-btn>
                  </div>

                  <div
                    v-if="!activeCompetitionGroups(specialty).length"
                    class="competition-config__empty"
                  >
                    Приём по этой специальности не настроен. Во всех конкурсах количество
                    мест равно нулю.
                  </div>

                  <div v-else class="competition-config__competition-groups">
                    <section
                      v-for="group in activeCompetitionGroups(specialty)"
                      :key="group.study.id"
                      class="competition-config__competition-group"
                    >
                      <div class="competition-config__competition-study">
                        {{ group.study.name }}
                      </div>

                      <div class="competition-config__competition-list">
                        <article
                          v-for="param in group.params"
                          :key="param.id"
                          :competitivegroup="param.id"
                          :direction="param.direction"
                          class="competition-config__competition"
                        >
                          <div class="competition-config__competition-main">
                            <div class="competition-config__competition-label">
                              {{ param.label }}
                            </div>
                            <div class="competition-config__seats">
                              <v-chip color="primary" variant="tonal" size="small">
                                Бюджет: {{ param.budget }}
                              </v-chip>
                              <v-chip color="secondary" variant="tonal" size="small">
                                Договор: {{ param.contract }}
                              </v-chip>
                              <v-chip color="success" variant="tonal" size="small">
                                Целевые: {{ param.targetTotal }}
                              </v-chip>
                            </div>
                          </div>

                          <div class="competition-config__competition-actions">
                            <v-btn
                              icon="mdi-pencil-outline"
                              variant="text"
                              size="small"
                              aria-label="Редактировать конкурс"
                              @click="openEditor(specialty, group.study, param)"
                            />
                            <v-btn
                              icon="mdi-close-circle-outline"
                              variant="text"
                              color="error"
                              size="small"
                              aria-label="Отключить конкурс"
                              @click="openDisableDialog(specialty, group.study, param)"
                            />
                          </div>
                        </article>
                      </div>
                    </section>
                  </div>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </section>
        </div>
      </template>
    </v-card-text>

    <v-dialog v-model="editorDialog" max-width="820" persistent>
      <v-card>
        <v-card-title>
          {{ editedParamId ? 'Редактирование конкурсной группы' : 'Добавление конкурсной группы' }}
        </v-card-title>

        <v-card-subtitle v-if="editedSpecialty" class="pt-1">
          {{ editedSpecialty.code }} {{ editedSpecialty.name }}
        </v-card-subtitle>

        <v-card-text>
          <v-alert
            v-if="dialogError"
            class="mb-5"
            type="error"
            variant="tonal"
          >
            {{ dialogError }}
          </v-alert>

          <div
            v-if="editedParamId && selectedParam"
            class="competition-config__edited-competition"
          >
            <div class="competition-config__edited-competition-label">
              Конкурс
            </div>
            <div class="competition-config__edited-competition-description">
              {{ selectedParam.label }}
            </div>
          </div>

          <div class="competition-config__dialog-selects">
            <v-select
              v-model="editedStudyId"
              :items="studyOptions"
              item-title="name"
              item-value="id"
              label="Форма обучения"
              variant="outlined"
              :disabled="saving || Boolean(editedParamId)"
              hide-details="auto"
              @update:model-value="onStudyChanged"
            />

            <v-select
              v-model="selectedParamId"
              :items="competitionOptions"
              item-title="title"
              item-value="id"
              label="Конкурс"
              variant="outlined"
              :disabled="saving || !editedStudyId || Boolean(editedParamId)"
              hide-details="auto"
              @update:model-value="onParamChanged"
            />
          </div>

          <v-alert
            v-if="!editedParamId && editedStudyId && !competitionOptions.length"
            class="mt-5"
            type="info"
            variant="tonal"
          >
            Все доступные конкурсы для этой формы обучения уже добавлены.
          </v-alert>

          <v-alert
            v-if="selectedParam && !selectedParam.hasQualification"
            class="mt-5"
            type="warning"
            variant="tonal"
          >
            Для выбранного конкурса не указана квалификация в структуре образовательной
            организации. Сохранение недоступно.
          </v-alert>

          <template v-if="selectedParam">
            <h3 class="competition-config__form-heading">Основной набор</h3>
            <div class="competition-config__seat-grid">
              <v-text-field
                v-model.number="form.budget"
                type="number"
                min="0"
                step="1"
                label="Бюджетные места"
                variant="outlined"
                hide-details="auto"
              />
              <v-text-field
                v-model.number="form.contract"
                type="number"
                min="0"
                step="1"
                label="Договорные места"
                variant="outlined"
                hide-details="auto"
              />
            </div>

            <h3 class="competition-config__form-heading">
              Целевые места по организациям
            </h3>

            <v-alert
              v-if="!targetOrganizations.length"
              type="info"
              variant="tonal"
            >
              Сначала добавьте организации в разделе «Целевой набор».
            </v-alert>

            <div v-else class="competition-config__target-grid">
              <v-text-field
                v-for="organization in targetOrganizations"
                :key="organization.id"
                v-model.number="form.targets[organization.id]"
                type="number"
                min="0"
                step="1"
                :label="organization.name"
                variant="outlined"
                hide-details="auto"
              />
            </div>
          </template>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="saving" @click="closeEditor">Отмена</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :loading="saving"
            :disabled="!selectedParam || !selectedParam.hasQualification"
            @click="saveCompetition"
          >
            Сохранить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="disableDialog" max-width="520" persistent>
      <v-card>
        <v-card-title>Отключить конкурсную группу?</v-card-title>
        <v-card-text>
          Все бюджетные, договорные и целевые места этого конкурса будут установлены в
          ноль. Если в конкурсе уже есть заявления, отключение может быть запрещено.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="saving" @click="disableDialog = false">
            Отмена
          </v-btn>
          <v-btn color="error" variant="flat" :loading="saving" @click="disableCompetition">
            Отключить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
export default {
  name: 'SpecialtySection',
  data() {
    return {
      departments: [],
      targetOrganizations: [],
      selectedDepartmentId: 'all',
      loading: true,
      saving: false,
      error: '',
      dialogError: '',
      editorDialog: false,
      disableDialog: false,
      editedSpecialty: null,
      editedStudyId: null,
      editedParamId: null,
      selectedParamId: null,
      pendingDisable: null,
      form: {
        budget: 0,
        contract: 0,
        targets: {}
      }
    };
  },
  computed: {
    departmentOptions() {
      return [
        { id: 'all', name: 'Все' },
        ...this.departments
      ];
    },
    isAllDepartments() {
      return this.selectedDepartmentId === 'all';
    },
    displayedDepartmentGroups() {
      if (this.isAllDepartments) {
        return this.departments.filter(
          (department) => department.specialties.length
        );
      }

      const department = this.departments.find(
        (item) => Number(item.id) === Number(this.selectedDepartmentId)
      );
      return department ? [department] : [];
    },
    displayedSpecialtyCount() {
      return this.displayedDepartmentGroups.reduce(
        (count, department) => count + department.specialties.length,
        0
      );
    },
    studyOptions() {
      return this.editedSpecialty ? this.editedSpecialty.studies : [];
    },
    selectedStudy() {
      return this.studyOptions.find(
        (study) => Number(study.id) === Number(this.editedStudyId)
      ) || null;
    },
    competitionOptions() {
      if (!this.selectedStudy) {
        return [];
      }

      // При добавлении предлагаем только ещё не участвующие группы (все места
      // равны нулю); текущая группа остаётся в списке при редактировании.
      const availableParams = this.editedParamId
        ? this.selectedStudy.params
        : this.selectedStudy.params.filter(
          (param) => Number(param.totalSeats) === 0
        );

      return availableParams.map((param) => ({
        id: param.id,
        title: param.hasQualification
          ? param.label
          : `${param.label} — не указана квалификация`,
        props: {
          disabled: !param.hasQualification
        }
      }));
    },
    selectedParam() {
      if (!this.selectedStudy) {
        return null;
      }

      return this.selectedStudy.params.find(
        (param) => Number(param.id) === Number(this.selectedParamId)
      ) || null;
    }
  },
  created() {
    this.loadConfiguration();
  },
  methods: {
    loadConfiguration() {
      this.loading = true;
      this.error = '';

      this.axios
        .get(
          this.$model.wwwroot +
            '/procollege/manage_groups/admission/config/getSpecialtyCompetitionConfiguration'
        )
        .then((response) => {
          this.departments = response.data.departments || [];
          this.targetOrganizations = response.data.targetOrganizations || [];
          this.selectedDepartmentId = 'all';
        })
        .catch((error) => {
          this.error = 'Не удалось загрузить конфигурацию конкурсных групп.';
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    specialtyCompetitionCount(specialty) {
      return this.activeCompetitionGroups(specialty).reduce(
        (count, group) => count + group.params.length,
        0
      );
    },
    activeCompetitionGroups(specialty) {
      // Нулевые группы существуют в справочнике, но до назначения мест
      // не считаются участниками текущей приёмной кампании.
      return specialty.studies
        .map((study) => ({
          study,
          params: study.params.filter((param) => Number(param.totalSeats) > 0)
        }))
        .filter((group) => group.params.length);
    },
    openEditor(specialty, study = null, param = null) {
      this.editedSpecialty = specialty;
      this.editedStudyId = study ? study.id : null;
      this.editedParamId = param ? param.id : null;
      this.selectedParamId = param ? param.id : null;
      this.dialogError = '';
      this.resetForm();

      if (param) {
        this.fillForm(param);
      }

      this.editorDialog = true;
    },
    closeEditor() {
      this.editorDialog = false;
      this.dialogError = '';
    },
    onStudyChanged() {
      this.selectedParamId = null;
      this.resetForm();
    },
    onParamChanged() {
      this.resetForm();
      if (this.selectedParam) {
        this.fillForm(this.selectedParam);
      }
    },
    resetForm() {
      const targets = {};
      this.targetOrganizations.forEach((organization) => {
        targets[organization.id] = 0;
      });
      this.form = { budget: 0, contract: 0, targets };
    },
    fillForm(param) {
      const targets = {};
      this.targetOrganizations.forEach((organization) => {
        targets[organization.id] = this.normalizeSeats(
          param.targets ? param.targets[organization.id] : 0
        );
      });

      this.form = {
        budget: this.normalizeSeats(param.budget),
        contract: this.normalizeSeats(param.contract),
        targets
      };
    },
    normalizeSeats(value) {
      const number = Number(value);
      return Number.isFinite(number) && number > 0 ? Math.floor(number) : 0;
    },
    saveCompetition() {
      if (!this.selectedParam || !this.selectedParam.hasQualification) {
        this.dialogError = 'Выберите конкурс с указанной квалификацией.';
        return;
      }

      this.persistCompetition(
        this.selectedStudy,
        this.selectedParam,
        this.normalizeSeats(this.form.budget),
        this.normalizeSeats(this.form.contract),
        this.form.targets,
        () => {
          this.closeEditor();
        }
      );
    },
    openDisableDialog(specialty, study, param) {
      this.pendingDisable = { specialty, study, param };
      this.disableDialog = true;
    },
    disableCompetition() {
      if (!this.pendingDisable) {
        return;
      }

      const { study, param } = this.pendingDisable;
      this.persistCompetition(study, param, 0, 0, {}, () => {
        this.disableDialog = false;
        this.pendingDisable = null;
      });
    },
    persistCompetition(study, param, budget, contract, targets, onSuccess) {
      // Целевые значения передаются полным набором по организациям: сервер
      // атомарно заменяет прежнее распределение вместе с бюджетом и договором.
      const normalizedTargets = {};
      this.targetOrganizations.forEach((organization) => {
        normalizedTargets[organization.id] = this.normalizeSeats(
          targets[organization.id]
        );
      });

      this.saving = true;
      this.dialogError = '';
      this.error = '';

      this.axios
        .post(
          this.$model.wwwroot +
            '/procollege/manage_groups/admission/config/saveSpecialtyCompetition',
          new URLSearchParams({
            paramId: String(param.id),
            budget: String(budget),
            contract: String(contract),
            targets: JSON.stringify(normalizedTargets)
          })
        )
        .then((response) => {
          const saved = response.data.item;
          param.budget = saved.budget;
          param.contract = saved.contract;
          param.targets = saved.targets || {};
          param.targetTotal = saved.targetTotal;
          param.totalSeats = saved.totalSeats;
          onSuccess();
        })
        .catch((error) => {
          const message =
            'Не удалось сохранить конкурс. Проверьте связанные заявления и введённые значения.';

          if (this.editorDialog) {
            this.dialogError = message;
          } else {
            this.error = message;
            this.disableDialog = false;
          }
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          this.saving = false;
        });
    }
  }
};
</script>

<style scoped>
.competition-config {
  border: 1px solid #dfe5ec;
  border-radius: 12px;
}
.competition-config__header {
  padding: 24px 28px 18px;
  border-bottom: 1px solid #edf0f4;
  white-space: normal;
}
.competition-config__title {
  margin: 0;
  color: #1d2939;
  font-size: 22px;
  font-weight: 600;
}
.competition-config__description {
  margin: 6px 0 0;
  color: #667085;
  font-size: 14px;
  font-weight: 400;
}
.competition-config__content {
  padding: 24px 28px 28px;
}
.competition-config__alert {
  margin-bottom: 20px;
}
.competition-config__filters {
  max-width: 420px;
  margin-bottom: 20px;
}
.competition-config__department-groups {
  display: flex;
  flex-direction: column;
  gap: 28px;
}
.competition-config__department-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
  padding: 0 4px;
}
.competition-config__department-heading h3 {
  margin: 0;
  color: #344054;
  font-size: 18px;
  font-weight: 600;
}
.competition-config__specialties {
  gap: 12px;
}
.competition-config__specialty {
  border: 1px solid #e4e7ec;
  border-radius: 10px;
}
.competition-config__specialty-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  padding-right: 12px;
}
.competition-config__specialty-heading strong {
  color: #344054;
}
.competition-config__specialty-code {
  margin-right: 10px;
  color: #1565c0;
  font-weight: 600;
}
.competition-config__specialty-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}
.competition-config__study-list,
.competition-config__seats {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}
.competition-config__empty {
  padding: 18px;
  border-radius: 8px;
  background: #f8fafc;
  color: #667085;
  text-align: center;
}
.competition-config__competition-list {
  overflow: hidden;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
}
.competition-config__competition-groups {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.competition-config__competition {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 14px 16px;
  border-bottom: 1px solid #edf0f4;
}
.competition-config__competition:last-child {
  border-bottom: 0;
}
.competition-config__competition-study {
  margin: 0 0 6px 4px;
  color: #1565c0;
  font-size: 13px;
  font-weight: 600;
}
.competition-config__competition-label {
  color: #344054;
  line-height: 1.4;
}
.competition-config__seats {
  margin-top: 9px;
}
.competition-config__competition-actions {
  display: flex;
  flex: 0 0 auto;
}
.competition-config__edited-competition {
  margin-bottom: 20px;
  padding: 14px 16px;
  border: 1px solid #dbe7f3;
  border-radius: 8px;
  background: #f5f9fd;
}
.competition-config__edited-competition-label {
  margin-bottom: 4px;
  color: #1565c0;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}
.competition-config__edited-competition-description {
  color: #344054;
  line-height: 1.5;
  overflow-wrap: anywhere;
}
.competition-config__dialog-selects,
.competition-config__seat-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}
.competition-config__form-heading {
  margin: 24px 0 12px;
  color: #475467;
  font-size: 15px;
  font-weight: 600;
}
.competition-config__target-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}
@media (max-width: 760px) {
  .competition-config__dialog-selects,
  .competition-config__seat-grid,
  .competition-config__target-grid {
    grid-template-columns: 1fr;
  }
  .competition-config__specialty-toolbar,
  .competition-config__competition {
    align-items: stretch;
    flex-direction: column;
  }
  .competition-config__specialty-toolbar .v-btn {
    width: 100%;
  }
  .competition-config__competition-actions {
    justify-content: flex-end;
  }
}
@media (max-width: 600px) {
  .competition-config__header,
  .competition-config__content {
    padding-right: 18px;
    padding-left: 18px;
  }
}
</style>
